import SanitizeJs from '../service/_sanitizeJs'
import Collection from '../service/_collection'
import SeatClass from '../const/_seat-class'

import reduce from 'lodash/reduce'
import reject from 'lodash/reject'
import result from 'lodash/result'
import isEmpty from 'lodash/isEmpty'

import dayjs from 'dayjs'
import 'dayjs/locale/ja'
dayjs.locale(`ja`)

/**
 * isString. // 文字列か判定する
 *
 * @param {object} obj
 */
export function isString(obj) {
  return typeof obj === 'string' || obj instanceof String
}

export function isFunction(func) {
  return typeof func === 'function'
}

/**
 * isObject // {}のオブジェクトか判定する
 *
 * @param {object} val
 */
export function isObject(val) {
  if (val !== null && typeof val === 'object' && val.constructor === Object) {
    return true
  }
  return false
}

/**
 * isNaN. // NaNか判定する
 *
 * @param {} num
 */
export function isNaN(num) {
  return Number.isNaN(num)
}

/**
 * isNumber. // 数値か判定する
 *
 * @param {any} num
 */
export function isNumber(num) {
  num = Number(num)
  if (isNaN(num)) return false
  return true
}

/**
 * isArray.// 配列か判定する
 *
 * @param {array} array
 */
export function isArray(array) {
  return Array.isArray(array)
}

/**
 * isHttpUrl // httpもしくはhttpsで始まるurlか判定する
 *
 * @param {string} str
 */
export function isHttpUrl(str) {
  return str.match(/^https?:\/\//) !== null
}

/**
 * isAbsolutePath // pathの戦闘が/で始まっているか
 *
 * @param {string} str
 */
export function isAbsolutePath(str) {
  return str.match(/^\//) !== null
}

/**
 * isUrl // url形式か判定する
 *
 * @param {string} str
 */
export function isUrl(str) {
  let flg = false
  flg += isHttpUrl(str)
  flg += isAbsolutePath(str)

  return flg
}

/**
 * isStoringToNumber // 数値に変換できるか判定する
 *
 * @param {number} val
 * @param {boolean} bool = false // false ゼロ埋めなし true ゼロ埋め有り
 */
export function isStoringToNumber(val, bool = false) {
  let pattern = /^[+,-]?([1-9]\d*|0)(\.\d+)?$/
  if (bool) pattern = /^[+,-]?\d(\.\d+)?$/

  return pattern.test(val)
}

/**
 * objectToGet.// オブジェクトからデータを取得する
 *
 * @param {} object
 * @param {} namespace
 * @param {} defaultValue
 */
export function objectToGet(object, namespace, defaultValue) {
  if (namespace === '*') return object
  try {
    return reduce(
      reject(namespace.split('.'), isEmpty),
      function (object, key) {
        const value = result(object, key)
        if (value) return value
        if (value === 0) return value
        if (value === '0') return value
        if (value === false) return value
        throw new ReferenceError()
      },
      object,
    )
  } catch (e) {
    return defaultValue
  }
}

// get2(object, path, defaultValue) {
//   if (!object) return null
//   const propertyArray = path.split('.')
//   let result = object
//   try {
//     propertyArray.map(value => {
//       const r = result[value]
//       result = r
//     })
//     return result
//   } catch (e) {
//     return defaultValue
//   }
// }

/**
 * response.// apiのResponseから結果を取得する
 *
 * @param {object} data
 */
export function response(data) {
  const result = data.result
  if (result) return result
  return data
}

/**
 * sanitizeJs // 入力されたhtmlを解析し<script>タグを削除する
 *
 * @param {} html
 */
export function sanitizeJs(html) {
  if (html === null) return null
  if (isString(html)) {
    const data = SanitizeJs.htmlToData(html)
    const result = SanitizeJs.start(data)
    return SanitizeJs.dataToHtml(result)
  }
  return null
}

/**
 * sanitizeHtml // htmlを無害化 htmlspecialchars()
 *
 * @param {string} str
 */
export function sanitizeHtml(str) {
  if (!isString(str) && !isNumber(str)) throw new TypeError('引数が有効ではありません')
  return (str + '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * sanitizeUrl // XSS対策
 *
 * @param {string} url
 */
export function sanitizeUrl(url) {
  const checkUrl = isUrl(url)
  if (checkUrl) return sanitizeHtml(url)
  return ''
}

/**
 * url // URLを取得する
 *
 * @param {object} data
 * @param {string} name
 * @param {string} defaultValue
 */
export function url(data, name, defaultValue = null) {
  const url = objectToGet(data, name)

  // データが取れれば
  if (url) {
    const result = sanitizeUrl(url)
    // データの値が有効であれば返す
    if (result !== '') return result
  }
  return defaultValue
}

/**
 * text2br 改行コードをbrに変換し許可したタグ以外削除を行う
 *
 * @param {string} text
 */
export function text2br(text) {
  if (!isString(text)) throw new TypeError('引数がstringではありません')
  return sanitizeJs(nl2br(text))
}

/**
 * nl2br // 改行コードを<br>に変換する
 *
 * @param {string} str
 */
export function nl2br(str) {
  if (!isString(str)) throw new TypeError('引数がstringではありません')
  return str.replace(/\r?\n/g, '\n').replace(/\\n/g, '<br>')
}

/**
 * number. // 数値に変換
 *
 * @param {} data
 */
export function toNumber(data) {
  if (!isNumber(data)) throw new TypeError('引数が数値ではありません')
  return parseInt(data)
}

/**
 * number. // 端数あり数値に変換
 *
 * @param {} data
 */
export function toFloat(data) {
  if (!isFinite(data)) throw new TypeError('引数が端数あり数値ではありません')
  return parseFloat(data)
}

/**
 * html // 許可したタグ以外削除
 *
 * @param {string} text
 * @param {string} defaultValue
 */
export function htmlOrDefault(html, defaultValue = '') {
  if (html) return sanitizeJs(html)
  return defaultValue
}

/**
 * text htmlspecialcharsを行う
 *
 * @param {string} text
 * @param {string} defaultValue
 */
export function textOrDefault(text, defaultValue = '') {
  if (text || text === 0) return sanitizeHtml(text)
  return defaultValue
}

/**
 * text2br 改行コードをbrに変換し許可したタグ以外削除を行う
 *
 * @param {string} text
 * @param {string} defaultValue
 */
export function text2brOrDefault(text, defaultValue = '') {
  if (text) return text2br(text)
  return defaultValue
}

/**
 * objectToNumber // 数値を取得する parseIntあり
 *
 * @param {object} data
 * @param {string} name
 * @param {any} defaultValue
 */
export function objectToNumber(data, name, defaultValue = null) {
  const num = objectToGet(data, name, null)
  if (num) return parseInt(num)
  return defaultValue
}

/**
 * objectToHtml // 許可したタグ以外削除
 *
 * @param {object} data
 * @param {string} name
 * @param {string} defaultValue
 */
export function objectToHtml(data, name = '', defaultValue = '') {
  const html = objectToGet(data, name)
  if (html) return sanitizeJs(html)
  return defaultValue
}

/**
 * objectToText htmlspecialcharsを行う
 *
 * @param {object} data
 * @param {string} name
 * @param {string} defaultValue
 */
export function objectToText(data, name = '', defaultValue = '') {
  const text = objectToGet(data, name)
  if (text || text === 0) return sanitizeHtml(text)
  return defaultValue
}

/**
 * text2br 改行コードをbrに変換し許可したタグ以外削除を行う
 *
 * @param {object} data
 * @param {string} name
 * @param {string} defaultValue
 */
export function objectToTextBr(data, name = '', defaultValue = '') {
  const text = objectToGet(data, name)
  if (text) return text2br(text)
  return defaultValue
}

/**
 * arrayToPick. // 項目を抜き出す
 *
 * @param {} array
 * @param {} path // default ''
 * @param {} defaultValue
 */
export function arrayToPick(array, path = '', defaultValue) {
  if (!isArray(array)) throw new TypeError('引数が配列ではありません')
  return array.map(item => {
    return objectToGet(item, path, defaultValue)
  })
}

/**
 * objectArrayTotal.// 合計値を返す
 *
 * @param {} array
 * @param {} path
 */
export function objectArrayTotal(array, defaultValue = null) {
  if (!isArray(array)) throw new TypeError('引数が配列ではありません')
  let result = 0
  array.map(item => {
    result += parseInt(item)
  })
  return result
}

export function collect(array) {
  if (!isArray(array)) throw new TypeError('引数が配列ではありません')
  return new Collection(array)
}

export function log(data) {
  console.log('log', data)
  return data
}

/**
 * seatClassToText.// シートクラスを日本語表記に変換する
 *
 * @param {} key
 */
export function seatClassToText(key) {
  if (!isString(key)) throw new TypeError('引数が文字列ではありません')
  const item = SeatClass.find(item => item.string === key)
  return item.ja
}

/**
 * trigger // コントローラーのトリガーを引く
 *
 * @param {string} name // トリガー名
 */
export function trigger(name) {
  window.controller.trigger(name)
}

/**
 * makeParams // urlのgetパラメーターを作成する
 *
 * @param {string} url
 * @param {object} items { key: value }
 */
export function makeParams(url, items) {
  url = new URL(url, location.href)
  Object.keys(items).forEach(key => {
    if (items[key]) url.searchParams.set(key, items[key])
  })
  return url.toString()
}

/**
 * getParam. url パラメーターから値を取得する
 *
 * @param {} name
 * @param {} url
 */
export function getParam(name, url = null) {
  if (!url) url = window.location.href
  url = new URL(url)
  const params = new URLSearchParams(url.search)
  const result = params.get(name)
  if (result === null) return null
  return result
}

/**
 * urlParamsReplace. // パラメーターを書き換える
 *
 * @param {object} items { key: value }
 */
export function urlParamsReplace(items) {
  history.replaceState('', '', makeParams(location.href, items))
}

/**
 * randomStr // ランダムなidの文字列を返す
 *
 * @param {number} num // 桁数
 */
export function randomStr(num) {
  const S = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  return Array.from(Array(num))
    .map(() => S[Math.floor(Math.random() * S.length)])
    .join('')
}

/**
 * indexOfMax // インデックスの最大値を取得する
 *
 * @param {array} arr
 */
export function indexOfMax(arr) {
  if (arr.length === 0) {
    return -1
  }

  var max = arr.length
  var maxIndex = 0

  for (var i = 1; i < max; i++) {
    maxIndex = i
  }

  return maxIndex
}

/**
 * isSmartPhone // スマートフォンか判定
 */
export function isSmartPhone() {
  if (navigator.userAgent.match(/iPhone|Android.+Mobile/)) return true
  return false
}

/**
 * getCookieArray cookieの値を配列として取得
 */
export function getCookieArray() {
  const arr = []
  if (document.cookie !== '') {
    const tmp = document.cookie.split('; ')
    for (let i = 0; i < tmp.length; i++) {
      const data = tmp[i].split('=')
      arr[data[0]] = decodeURIComponent(data[1])
    }
  }
  return arr
}

/**
 * sign //  符号を判定
 *
 * @param {number} n
 */
export function sign(n) {
  if (n > 0) return 1
  else if (n < 0) return -1
  else return 0
}

/**
 * numberToArray. // 数値の配列を作成する
 *
 * @param {number} num
 * @param {number} min
 */
export function numberToArray(num = 0, min = 0) {
  const result = []
  for (let i = min; i <= num; i++) {
    result.push(i)
  }
  return result
}

/**
 * numManager. // 数を丸める
 *
 * @param {number} min
 * @param {number} max
 * @param {number} num
 */
export function numManager(min = 0, max = 0, num) {
  num %= max
  if (num < min) {
    num += max
  }
  return num
}

// ============================
// イベント処理
// ============================

export function setEvent(html, that) {
  let fragment = document.createDocumentFragment()
  fragment = $(fragment).append(html)

  // フック処理
  fragment
    .find('[f]')
    .each((index, element) => {
      const f = $(element).attr('f')
      that.hook(f, fragment)
    })
    .removeAttr('f')

  // clickイベントを設定
  fragment
    .find('[c]')
    .each((index, element) => {
      const c = $(element).attr('c')
      const d = $(element).attr('d')
      const func = that[c].bind(that)
      if (func && isFunction(func)) $(element).on('click', () => func(d))
    })
    .removeAttr('c')
  // .removeAttr('d')
  return fragment
}

export function setSwipeX(element, func, offset = 0) {
  let startX = null
  let endX = null

  element.on('touchmove', event => {
    // event.preventDefault()
    startX = event.touches[0].pageX
  })
  element.on('touchstart', event => {
    // event.preventDefault()
    endX = event.touches[0].pageX
  })
  element.on('touchend', event => {
    // event.preventDefault()
    console.log('スワイプオフセット', offset)
    const moveX = endX - startX
    console.log('スワイプ距離', moveX)
    console.log('スワイプ判定左', moveX > offset)
    console.log('スワイプ判定右', moveX < -offset)
    if (moveX > offset) {
      func('left')
    }
    if (moveX < -offset) {
      func('right')
    }
  })
}

// ============================
// dayjs
// ============================

/**
 * dateFormat 日付の文字列を指定のformatにして返す
 *
 * @param {string} date // 2020-11-01
 * @param {string} format // 表示するフォーマット
 */
export function dateFormat(date, format) {
  return dayjs(date).format(format)
}

/**
 * now 今
 *
 */
export function now() {
  return dayjs()
}

/**
 * date // dayjsのインスタンスを返す
 *
 * @param {string} date
 */
export function dayJs(date) {
  return dayjs(date)
}

/**
 * isDayJs // dayjsのインスタンスか判定する
 *
 * @param {any} data
 */
export function isDayJs(data) {
  return dayjs.isDayjs(data)
}

/**
 * isSameDay // 同じ日付か比較する
 *
 * @param {string} date1 // 2020-12-01
 * @param {string} date2 // 2020-12-01
 */
export function isSameDay(date1, date2) {
  return dayjs(date1).isSame(dayjs(date2), 'day')
}

// ============================
// その他
// ============================

/**
 * isDisplayNumber // 表示するナンバーを判定する関数
 *
 * @param {number} number
 * @param {number} current
 * @param {number} min
 * @param {number} max
 * @return boolean
 */
export function isDisplayNumber(number, current, min, max) {
  const maxNum = current + 2
  const minNum = current - 2
  if (min === number || max === number || current === number) return true
  if (minNum <= number && maxNum >= number) return true
  return false
}

/**
 * isDisplaySymbol // Symbolを表示する判定を行う
 *
 * @param {number} number
 * @param {number} current
 * @param {number} min
 * @param {number} max
 * @return boolean
 */
export function isDisplaySymbol(number, current, min, max) {
  const maxNum = current + 3
  const minNum = current - 3
  if (min === number || max === number) return false
  if (maxNum === number || minNum === number) return true
  return false
}

export function saveData(key, data) {
  const saveData = JSON.stringify(data)
  localStorage.setItem(key, saveData)
}

export function loadData(key) {
  const data = localStorage.getItem(key)
  if (data) return JSON.parse(data)
  return null
}

export function removeData(key) {
  localStorage.removeItem(key)
}

export function deviceWidthSm(windowSm = 0) {
  const windowWidth = $(window).width()
  if (windowWidth <= windowSm) {
    return true
  } else {
    return false
  }
}
